/**
 * Hash of empty EVM bytecode (Keccak256 of empty bytes).
 *
 * This is a well-known constant in Ethereum representing the Keccak256 hash
 * of an empty byte array. It's used to identify accounts with no associated
 * contract code.
 *
 * Value: Keccak256("") = 0xc5d2460186f7233c927e7db2dcc703c0e500b653ca82273b7bfad8045d85a470
 *
 * @see https://voltaire.tevm.sh/primitives/state for State documentation
 * @since 0.0.0
 * @type {import('../Hash/index.js').HashType}
 * @example
 * ```javascript
 * import { EMPTY_CODE_HASH } from './primitives/State/index.js';
 * if (codeHash.equals(EMPTY_CODE_HASH)) {
 *   // Account has no code
 * }
 * ```
 */
export const EMPTY_CODE_HASH =
	/** @type {import('../Hash/index.js').HashType} */ (
		new Uint8Array([
			0xc5, 0xd2, 0x46, 0x01, 0x86, 0xf7, 0x23, 0x3c, 0x92, 0x7e, 0x7d, 0xb2,
			0xdc, 0xc7, 0x03, 0xc0, 0xe5, 0x00, 0xb6, 0x53, 0xca, 0x82, 0x27, 0x3b,
			0x7b, 0xfa, 0xd8, 0x04, 0x5d, 0x85, 0xa4, 0x70,
		])
	);

/**
 * Root hash of an empty Merkle Patricia Trie.
 *
 * This is the root hash of an empty trie structure in Ethereum, used as
 * the initial value for account storage roots and state roots when they
 * contain no data.
 *
 * Value: Keccak256(RLP(null)) = 0x56e81f171bcc55a6ff8345e692c0f86e5b48e01b996cadc001622fb5e363b421
 *
 * @see https://voltaire.tevm.sh/primitives/state for State documentation
 * @since 0.0.0
 * @type {import('../Hash/index.js').HashType}
 * @example
 * ```javascript
 * import { EMPTY_TRIE_ROOT } from './primitives/State/index.js';
 * if (storageRoot.equals(EMPTY_TRIE_ROOT)) {
 *   // Account has no storage
 * }
 * ```
 */
export const EMPTY_TRIE_ROOT =
	/** @type {import('../Hash/index.js').HashType} */ (
		new Uint8Array([
			0x56, 0xe8, 0x1f, 0x17, 0x1b, 0xcc, 0x55, 0xa6, 0xff, 0x83, 0x45, 0xe6,
			0x92, 0xc0, 0xf8, 0x6e, 0x5b, 0x48, 0xe0, 0x1b, 0x99, 0x6c, 0xad, 0xc0,
			0x01, 0x62, 0x2f, 0xb5, 0xe3, 0x63, 0xb4, 0x21,
		])
	);
